import { ExtensionKit } from "@/components/extensions/extension-kit";
import type { MessageRouting } from "@/components/Providers/store";
import { isTemplateLoadingAtom } from "@/components/Providers/store";
import {
  templateEditorAtom,
  templateEditorContentAtom,
  isTemplateTransitioningAtom,
  pendingAutoSaveAtom,
  getFormUpdating,
  previewLocaleAtom,
} from "@/components/TemplateEditor/store";
import type { TextMenuConfig } from "@/components/ui/TextMenu/config";
import { selectedNodeAtom } from "@/components/ui/TextMenu/store";
import type { TiptapDoc } from "@/lib/utils";
import {
  applyLocaleToContent,
  convertElementalToTiptap,
  convertTiptapToElemental,
  updateElemental,
  createTitleUpdate,
} from "@/lib/utils";
// Imported from the module rather than the barrel: Inbox.test.tsx mocks
// "@/lib/utils" wholesale, and this helper is pure.
import { extractPlainTextFromNode } from "@/lib/utils/getTitle/preserveStorageFormat";
import { setTestEditor } from "@/lib/testHelpers";
import type { ChannelType } from "@/store";
import type { ElementalNode } from "@/types/elemental.types";
import type { AnyExtension, Editor } from "@tiptap/react";
import { useCurrentEditor } from "@tiptap/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import type { HTMLAttributes } from "react";
import { forwardRef, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { MainLayout } from "../../../ui/MainLayout";
import type { TemplateEditorProps } from "../../TemplateEditor";
import { Channels } from "../Channels";

export const defaultInboxContent: ElementalNode[] = [
  { type: "text", content: "\n", text_style: "h2" },
  { type: "text", content: "\n" },
  {
    type: "action",
    content: "Enter text",
    align: "left",
    href: "",
  },
];

// Helper function to get or create default inbox element
// Inbox structure: 1 Header (h2), 1 Body paragraph, optional action buttons
export const getOrCreateInboxElement = (
  templateEditorContent: { elements: ElementalNode[] } | null | undefined
): ElementalNode => {
  let element: ElementalNode | undefined = templateEditorContent?.elements.find(
    (el: ElementalNode): el is ElementalNode & { type: "channel"; channel: "inbox" } =>
      el.type === "channel" && el.channel === "inbox"
  );

  if (!element) {
    element = {
      type: "channel",
      channel: "inbox",
      elements: defaultInboxContent,
    };
  } else if (element.type === "channel" && "elements" in element) {
    // Convert stored format to editor format
    // Inbox always has: 1 Header (h2), 1 Body paragraph, optional action buttons
    const elements = element.elements || [];
    const metaElement = elements.find((el: ElementalNode) => el.type === "meta");
    const otherElements = elements.filter((el: ElementalNode) => el.type !== "meta");

    // Separate text elements from action elements
    const textElements = otherElements.filter((el: ElementalNode) => el.type === "text");
    const actionElements = otherElements.filter((el: ElementalNode) => el.type === "action");

    // Build the fixed structure: 1 header + 1 body + actions.
    //
    // The title has lived in three places across the template history, and
    // version history can hand us any of them. `getSubjectStorageFormat` knows
    // the first two; the third predates both. Reading only `meta` rendered older
    // versions with an empty title — and in the h2 case promoted the title into
    // the body slot and dropped the real body.
    const metaTitle = metaElement && "title" in metaElement ? metaElement.title || "" : "";
    const rawTitle =
      "raw" in element && element.raw && "title" in element.raw
        ? (element.raw as { title?: string }).title || ""
        : "";

    // Legacy: the title kept as the leading h2 text element rather than lifted
    // out. Only treat it as the title when neither of the newer homes has one,
    // so current content is unaffected.
    const leading = textElements[0];
    const leadingIsHeading = leading && "text_style" in leading && leading.text_style === "h2";
    const useLeadingAsTitle = !metaTitle && !rawTitle && Boolean(leadingIsHeading);

    // Read the text through extractPlainTextFromNode rather than off `.content`
    // directly: a node may legitimately carry its text in the rich `elements`
    // form instead. applyLocaleToContent produces exactly that when a locale
    // override supplies `elements`, and a `"content" in node` check then found
    // nothing and rendered the localized preview blank.
    const titleContent = useLeadingAsTitle
      ? leading
        ? extractPlainTextFromNode(leading)
        : ""
      : metaTitle || rawTitle;

    // Header element (h2)
    const headerElement = {
      type: "text" as const,
      content: titleContent + "\n",
      text_style: "h2" as const,
    };

    // Body element - the first text element that was not consumed as the title.
    // Only keep ONE body paragraph.
    const firstBodyText = textElements[useLeadingAsTitle ? 1 : 0];
    const bodyElement = {
      type: "text" as const,
      content: (firstBodyText ? extractPlainTextFromNode(firstBodyText) : "") || "\n",
    };

    const updatedElement: ElementalNode = {
      ...element,
      elements: [headerElement, bodyElement, ...actionElements],
    };
    element = updatedElement;
  }

  return element!;
};

export const InboxConfig: TextMenuConfig = {
  contentType: { state: "hidden" },
  bold: { state: "hidden" },
  italic: { state: "hidden" },
  underline: { state: "hidden" },
  strike: { state: "hidden" },
  alignLeft: { state: "hidden" },
  alignCenter: { state: "hidden" },
  alignRight: { state: "hidden" },
  alignJustify: { state: "hidden" },
  quote: { state: "hidden" },
  link: { state: "hidden" },
  variable: { state: "enabled" },
};

interface InboxEditorContentProps {
  value?: TiptapDoc;
}

export const InboxEditorContent = ({ value }: InboxEditorContentProps) => {
  const { editor } = useCurrentEditor();
  const setTemplateEditor = useSetAtom(templateEditorAtom);
  const templateEditorContent = useAtomValue(templateEditorContentAtom);
  const isTemplateLoading = useAtomValue(isTemplateLoadingAtom);
  const isValueUpdated = useRef(false);

  useEffect(() => {
    if (isTemplateLoading) {
      isValueUpdated.current = false;
    }
  }, [isTemplateLoading]);

  useEffect(() => {
    if (!editor || isTemplateLoading !== false || isValueUpdated.current || !value) {
      return;
    }

    isValueUpdated.current = true;

    editor.commands.setContent(value);
  }, [editor, value, isTemplateLoading]);

  useEffect(() => {
    if (editor) {
      setTemplateEditor(editor);
      setTestEditor("inbox", editor);
      setTimeout(() => {
        editor.commands.blur();
      }, 1);
    }
  }, [editor, setTemplateEditor]);

  // Update editor content when templateEditorContent changes
  useEffect(() => {
    if (!editor || !templateEditorContent) return;

    // Don't update content if user is actively typing
    if (editor.isFocused) return;

    // Don't update content if a sidebar form is actively updating the editor
    if (getFormUpdating()) return;

    // Don't update content if user is focused on a sidebar form input
    const activeElement = document.activeElement;
    if (activeElement?.closest("[data-sidebar-form]")) return;

    const element = getOrCreateInboxElement(templateEditorContent);

    const newContent = convertElementalToTiptap(
      {
        version: "2022-01-01",
        elements: [element],
      },
      { channel: "inbox" }
    );

    const incomingContent = convertTiptapToElemental(newContent);
    const currentContent = convertTiptapToElemental(editor.getJSON() as TiptapDoc);

    // Only update if content has actually changed to avoid infinite loops
    if (JSON.stringify(incomingContent) !== JSON.stringify(currentContent)) {
      setTimeout(() => {
        const activeEl = document.activeElement;
        const sidebarFocused = activeEl?.closest("[data-sidebar-form]") !== null;
        if (!editor.isFocused && !getFormUpdating() && !sidebarFocused) {
          editor.commands.setContent(newContent);
        }
      }, 1);
    }
  }, [editor, templateEditorContent]);

  return null;
};

export interface InboxRenderProps {
  content: TiptapDoc;
  extensions: AnyExtension[];
  editable: boolean;
  autofocus: boolean;
  onUpdate: ({ editor }: { editor: Editor }) => void;
}

export interface InboxProps
  extends Pick<
      TemplateEditorProps,
      | "hidePublish"
      | "theme"
      | "variables"
      | "disableVariablesAutocomplete"
      | "channels"
      | "routing"
      | "value"
      | "colorScheme"
    >,
    Omit<HTMLAttributes<HTMLDivElement>, "value" | "onChange"> {
  readOnly?: boolean;
  headerRenderer?: ({
    hidePublish,
    channels,
    routing,
  }: {
    hidePublish?: boolean;
    channels?: ChannelType[];
    routing?: MessageRouting;
  }) => React.ReactNode;
  render?: (props: InboxRenderProps) => React.ReactNode;
}

const InboxComponent = forwardRef<HTMLDivElement, InboxProps>(
  (
    {
      theme,
      hidePublish,
      readOnly,
      channels,
      routing,
      headerRenderer,
      render,
      value,
      colorScheme,
      variables,
      disableVariablesAutocomplete = false,
      ...rest
    },
    ref
  ) => {
    const isTemplateLoading = useAtomValue(isTemplateLoadingAtom);
    const previewLocale = useAtomValue(previewLocaleAtom);
    const isInitialLoadRef = useRef(true);
    const isMountedRef = useRef(false);
    const setSelectedNode = useSetAtom(selectedNodeAtom);
    const [templateEditorContent, setTemplateEditorContent] = useAtom(templateEditorContentAtom);
    const setPendingAutoSave = useSetAtom(pendingAutoSaveAtom);
    const isTemplateTransitioning = useAtomValue(isTemplateTransitioningAtom);

    // Track component mount status
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    const extensions = useMemo(
      () =>
        [
          ...ExtensionKit({
            setSelectedNode,
            variables,
            disableVariablesAutocomplete,
            textMarks: "plain-text", // In-App doesn't support rich text formatting
          }),
        ].filter((e): e is AnyExtension => e !== undefined),
      [setSelectedNode, variables, disableVariablesAutocomplete]
    );

    const onUpdateHandler = useCallback(
      ({ editor }: { editor: Editor }) => {
        if (!editor || isTemplateTransitioning) {
          return;
        }

        // Handle new templates by creating initial structure
        if (!templateEditorContent) {
          const elemental = convertTiptapToElemental(editor.getJSON() as TiptapDoc);

          const titleUpdate = createTitleUpdate(
            null, // No existing content for new template
            "inbox",
            "", // Empty fallback - let function extract from first element
            elemental
          );

          const newContent = {
            version: "2022-01-01" as const,
            elements: [
              {
                type: "channel" as const,
                channel: "inbox" as const,
                elements: titleUpdate.elements,
                ...(titleUpdate.raw && { raw: titleUpdate.raw }),
              },
            ],
          };
          setTemplateEditorContent(newContent);
          setPendingAutoSave(newContent);
          return;
        }

        // Prevent updates during rapid typing by debouncing
        const currentJson = editor.getJSON() as TiptapDoc;
        const elemental = convertTiptapToElemental(currentJson);

        // For Inbox, let createTitleUpdate extract title from first element
        // Don't pass the old title - let it extract from the new editor content
        const titleUpdate = createTitleUpdate(
          templateEditorContent,
          "inbox",
          "", // Empty fallback - let function extract from first element
          elemental
        );

        const newContent = updateElemental(templateEditorContent, {
          elements: titleUpdate.elements,
          channel: {
            channel: "inbox",
            // Always pass `raw` — for inbox it is always undefined, which tells
            // updateElemental to DROP any pre-existing `raw.title`. Templates saved by
            // older designer builds carry an un-interpolated `raw.title` that shadows
            // meta.title in the backend renderer; this heals them on the next edit.
            raw: titleUpdate.raw,
          },
        });

        if (JSON.stringify(templateEditorContent) !== JSON.stringify(newContent)) {
          setTemplateEditorContent(newContent);
          setPendingAutoSave(newContent);
        }
      },
      [templateEditorContent, setTemplateEditorContent, setPendingAutoSave, isTemplateTransitioning]
    );

    // While read-only — version history, Preview & Test — the host swaps `value`
    // to show a different saved version, and re-deriving is the only way that
    // selection reaches the canvas (ReadOnlyEditorContent re-applies whatever
    // this memo returns). Nothing is being typed, so it is safe. While editable
    // the memo stays frozen: see the dependency note below.
    const readOnlyValue = readOnly ? (value ?? templateEditorContent) : null;

    // Derive content once on mount - EditorProvider uses this as initial value only
    // Subsequent updates flow through restoration effect in InboxEditorContent
    const content = useMemo(() => {
      if (isTemplateLoading !== false) {
        return null;
      }

      // Use value prop first, fallback to templateEditorContent (like SMS and Push do)
      let sourceContent = value ?? templateEditorContent;

      // Apply locale translations BEFORE extracting the inbox element, because
      // getOrCreateInboxElement restructures child elements and drops locales.
      if (previewLocale && sourceContent) {
        sourceContent = applyLocaleToContent(sourceContent, previewLocale) ?? sourceContent;
      }

      const element = getOrCreateInboxElement(sourceContent);

      const elementalForConversion = {
        version: "2022-01-01" as const,
        elements: [element],
      };

      return convertElementalToTiptap(elementalForConversion, { channel: "inbox" });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTemplateLoading, previewLocale, readOnlyValue]); // `value`/`templateEditorContent` are read but intentionally omitted from the deps while editable: EditorProvider treats `content` as an initial value and live edits flow back out through onUpdate, so re-deriving mid-edit would fight the user's cursor. `readOnlyValue` re-admits `value` only when read-only.

    return (
      <MainLayout
        theme={theme}
        colorScheme={colorScheme}
        isLoading={Boolean(isTemplateLoading && isInitialLoadRef.current)}
        readOnly={readOnly}
        Header={
          headerRenderer ? (
            headerRenderer({ hidePublish, channels, routing })
          ) : (
            <Channels hidePublish={hidePublish} channels={channels} routing={routing} />
          )
        }
        {...rest}
        ref={ref}
      >
        {render?.({
          content: content!,
          extensions,
          editable: !readOnly,
          autofocus: false,
          onUpdate: onUpdateHandler,
        })}
        {/* <div className="courier-flex courier-flex-1 courier-flex-row courier-overflow-hidden">
          <div className="courier-flex courier-flex-col courier-flex-1 courier-py-8 courier-items-center">
            <div
              className="courier-py-2 courier-border courier-w-[360px] courier-h-[500px] courier-rounded-3xl courier-bg-background"
              style={{
                maskImage: "linear-gradient(180deg, #000 80%, transparent)",
                WebkitMaskImage: "linear-gradient(180deg, #000 80%, transparent)",
              }}
            >
              <div className="courier-my-3 courier-mx-4 courier-flex courier-items-center courier-gap-2 courier-justify-between">
                <div className="courier-flex courier-items-center courier-gap-3">
                  <InboxIcon />
                  <span className="courier-text-xl courier-font-medium">In-app</span>
                </div>

                <div className="courier-flex courier-items-center courier-gap-4">
                  <HamburgerMenuIcon />
                  <ExpandIcon />
                  <MoreMenuIcon />
                </div>
              </div>
              <EditorProvider
                content={content}
                extensions={extensions}
                editable={!readOnly}
                autofocus={!readOnly}
                onUpdate={onUpdateHandler}
                editorContainerProps={{
                  className: cn(
                    "courier-inbox-editor"
                    // readOnly && "courier-brand-editor-readonly"
                  ),
                }}
                immediatelyRender={false}
              >
                <InboxEditorContent />
                <BubbleTextMenu config={InboxConfig} />
              </EditorProvider>
            </div>
          </div>
          <div className="courier-editor-sidebar courier-opacity-100 courier-translate-x-0 courier-w-64 courier-flex-shrink-0">
            <div className="courier-p-4 courier-h-full">
              <SideBar />
            </div>
          </div>
        </div> */}
      </MainLayout>
    );
  }
);

export const Inbox = memo(InboxComponent);
